// Complete ColumnMappingStep with Template Management
// Copy this entire file to replace your existing ColumnMappingStep_FINAL.tsx

import React, { useState, useEffect } from 'react';
import Button from '../../UI/Button';
import Select from '../../UI/Select';
import Loader from '../../UI/Loader';
import { CSVColumn, ColumnMapping } from '../ImportWizard';
import { fetchReferenceData, isMappableAdditionalInfoField, ReferenceData } from '../../../services/Referencedataservice';
import { 
    getTemplates, 
    saveTemplate, 
    deleteTemplate, 
    updateTemplateLastUsed,
    templateNameExists,
    MappingTemplate 
} from '../../../services/mappingTemplatesService';
import toast from 'react-hot-toast';
import states from '../../../constants/states';
import { format } from 'date-fns';
import { householdIdentifiersEnabled, optionalDateOfBirthEnabled } from '../../../utils/features';
import { getEthnicityFieldVisible, getRaceFieldLabel, isDemographicVisible } from '../../../utils/demographicsSchema';

interface ColumnMappingStepProps {
    csvColumns: CSVColumn[];
    csvData: any[];
    onNext: (mappings: ColumnMapping[], referenceData: ReferenceData) => void;
    onBack: () => void;
    savedMappings?: ColumnMapping[];
    savedReferenceData?: ReferenceData;
}

const CORE_PARTICIPANT_FIELDS = [
    { value: '', label: 'Do Not Import' },
    { value: 'firstName', label: 'First Name *' },
    { value: 'middleName', label: 'Middle Name' },
    { value: 'lastName', label: 'Last Name *' },
    { value: 'dateOfBirth', label: 'Date of Birth' },
    { value: 'email', label: 'Email' },
    { value: 'phoneNumber', label: 'Phone Number' },
    { value: 'genderId', label: 'Gender' },
    { value: 'ethnicityId', label: 'Ethnicity' },
    { value: 'raceIds', label: 'Race(s)' },
    { value: 'pronouns', label: 'Pronouns' },
    { value: 'addressLine1', label: 'Address Line 1' },
    { value: 'addressLine2', label: 'Address Line 2' },
    { value: 'addressCity', label: 'City' },
    { value: 'addressState', label: 'State' },
    { value: 'addressZipCode', label: 'Zip Code' },
    { value: 'programName', label: 'Program Name' },
    { value: 'enrollmentDate', label: 'Enrollment Date' },
    { value: 'activationDate', label: 'Activation Date' },
    { value: 'householdKey', label: 'Household Key' },
    { value: 'householdId', label: 'Household ID' },
];

const ACTIVATION_DATE_TODAY_COLUMN = '__activationDateToday';

const isConstantMapping = (mapping: ColumnMapping) => mapping.sourceType === 'constant';
const getEnabledParticipantField = (participantField?: string, availableFields?: Set<string>) => {
    if (!householdIdentifiersEnabled && participantField === 'householdId') {
        return '';
    }

    if (participantField && availableFields && !availableFields.has(participantField)) {
        return '';
    }

    return participantField || '';
};

const getTodayDisplayValue = () => format(new Date(), 'MM/dd/yyyy');

const normalizeColumnNameForMatch = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const isActivationDateLikeColumn = (value: string) => {
    const normalized = normalizeColumnNameForMatch(value);
    return ['activationdate', 'activateddate', 'activedate'].some((candidate) => normalized.includes(candidate));
};

const HH_FIELD_TEMPLATES = [
    'firstName', 'middleName', 'lastName', 'dateOfBirth',
    'genderId', 'ethnicityId', 'raceIds', 'relationshipId',
    'phoneNumber', 'email','linkedClientId',
];

const getVisibleCoreParticipantFields = (refData: ReferenceData) => {
    return CORE_PARTICIPANT_FIELDS
        .filter(field => householdIdentifiersEnabled || field.value !== 'householdId')
        .filter(field => {
            if (field.value === 'pronouns') {
                return isDemographicVisible(refData.organization, 'pronouns');
            }

            if (field.value === 'genderId') {
                return isDemographicVisible(refData.organization, 'gender');
            }

            if (field.value === 'ethnicityId') {
                return getEthnicityFieldVisible(refData.organization);
            }

            if (field.value === 'raceIds') {
                return isDemographicVisible(refData.organization, 'race');
            }

            if (field.value === 'addressLine1' || field.value === 'addressLine2') {
                return isDemographicVisible(refData.organization, 'address');
            }

            if (field.value === 'addressCity') {
                return isDemographicVisible(refData.organization, 'city');
            }

            if (field.value === 'addressState') {
                return isDemographicVisible(refData.organization, 'state');
            }

            if (field.value === 'addressZipCode') {
                return isDemographicVisible(refData.organization, 'zip');
            }

            if (field.value === 'phoneNumber') {
                return isDemographicVisible(refData.organization, 'phone');
            }

            if (field.value === 'email') {
                return isDemographicVisible(refData.organization, 'email');
            }

            return true;
        })
        .map(field => {
            if (field.value === 'dateOfBirth' && !optionalDateOfBirthEnabled) {
                return { ...field, label: 'Date of Birth *' };
            }

            if (field.value === 'raceIds') {
                return { ...field, label: getRaceFieldLabel(refData.organization) };
            }

            return field;
        });
};

const getVisibleHouseholdFieldTemplates = (refData: ReferenceData) => {
    return HH_FIELD_TEMPLATES.filter(field => {
        if (field === 'genderId') {
            return isDemographicVisible(refData.organization, 'gender');
        }

        if (field === 'ethnicityId') {
            return getEthnicityFieldVisible(refData.organization);
        }

        if (field === 'raceIds') {
            return isDemographicVisible(refData.organization, 'race');
        }

        if (field === 'phoneNumber') {
            return isDemographicVisible(refData.organization, 'phone');
        }

        if (field === 'email') {
            return isDemographicVisible(refData.organization, 'email');
        }

        return true;
    });
};

const ColumnMappingStep: React.FC<ColumnMappingStepProps> = ({
    csvColumns, csvData, onNext, onBack, savedMappings, savedReferenceData,
}) => {
    const [mappings, setMappings] = useState<ColumnMapping[]>([]);
    const [unmappedColumns, setUnmappedColumns] = useState<CSVColumn[]>([]);
    const [participantFields, setParticipantFields] = useState<Array<{value: string, label: string}>>([]);
    const [householdCount, setHouseholdCount] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [referenceData, setReferenceData] = useState<ReferenceData | null>(null);
    
    // Template management state
    const [templates, setTemplates] = useState<MappingTemplate[]>([]);
    const [showSaveTemplate, setShowSaveTemplate] = useState(false);
    const [showLoadTemplate, setShowLoadTemplate] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');

    useEffect(() => {
        initializeMapping();
        loadTemplates();
    }, [csvColumns]);

    const loadTemplates = () => {
        const savedTemplates = getTemplates();
        setTemplates(savedTemplates);
    };

    const getFieldTypeSample = (field: any): string => {

        switch (field.fieldTypeId) {

            case 1:
            case 9:
            case 16:        
                return 'Text';

            case 7:
                return 'Multiline Text';

            case 2:
                return 'Number';

            // Radio, Select, Checkbox Group
            case 3:
            case 6:
            case 5:
                if (field.options && field.options.length > 0) {
                    return field.options
                        .map((opt: any) => opt.label) // ✅ FIX HERE
                        .join(', ');
                }
                return 'Options';

            case 4:
                return 'Checkbox';

            case 13:
                return 'Date';

            case 11:
                return 'Notes';

            case 12:
                return 'Minutes';

            default:
                return '';
        }
    };

    const initializeMapping = async () => {
        setLoading(true);
        
        try {
            // Use saved reference data if available, otherwise fetch
            const refData = savedReferenceData?.organization && savedReferenceData.genderOptions
                ? savedReferenceData
                : await fetchReferenceData();
            setReferenceData(refData);

            const hhPattern = /^HH(\d+)_/i;
            const hhNumbers = new Set<number>();

            csvColumns.forEach((column) => {
                const match = column.name.match(hhPattern);
                if (match) {
                    hhNumbers.add(parseInt(match[1]));
                }
            });

            const maxHH = hhNumbers.size > 0 ? Math.max(...Array.from(hhNumbers)) : 0;
            setHouseholdCount(maxHH);

            const allFields = getVisibleCoreParticipantFields(refData);

            if (refData.clientform && refData.clientform.length > 0) {
                refData.clientform.forEach(field => {
                    if(isMappableAdditionalInfoField(field)){
                        allFields.push({
                            value: `additionalInfo_${field.id}`,
                            label: `Additional Info - ${field.label}`,
                        });
                    }
                });
            }

            const householdFieldTemplates = getVisibleHouseholdFieldTemplates(refData);
            for (let hhNum = 1; hhNum <= maxHH; hhNum++) {
                householdFieldTemplates.forEach(field => {
                    const label = formatHouseholdFieldLabel(hhNum, field, refData);
                    allFields.push({
                        value: `hh${hhNum}_${field}`,
                        label: `HH${hhNum} - ${label}`,
                    });
                });
            }
            
            setParticipantFields(allFields);
            const availableFieldValues = new Set(allFields.map(field => field.value));
            
            // Use saved mappings if available, otherwise auto-map
            if (savedMappings && savedMappings.length > 0) {
                const restoredCsvMappings = csvColumns.map((column) => {
                    const savedMapping = savedMappings.find(
                        (mapping) => !isConstantMapping(mapping) && mapping.csvColumn === column.name
                    );

                    return savedMapping ? {
                        ...savedMapping,
                        participantField: getEnabledParticipantField(savedMapping.participantField, availableFieldValues),
                    } : {
                        csvColumn: column.name,
                        participantField: '',
                        sourceType: 'csv' as const,
                    };
                });
                const restoredMappings = [
                    ...restoredCsvMappings,
                    ...savedMappings.filter(isConstantMapping).map(mapping => ({
                        ...mapping,
                        participantField: getEnabledParticipantField(mapping.participantField, availableFieldValues),
                    })),
                ];

                setMappings(restoredMappings);
                updateUnmappedColumns(restoredMappings);
                toast.success('Restored previous column mappings');
            } else {
                autoMapColumns(allFields, refData);
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error initializing mapping:', error);
            toast.error('Failed to load reference data');
            setLoading(false);
        }
    };

    const formatHouseholdFieldLabel = (hhNum: number, field: string, refData: ReferenceData): string => {
        const labelMap: Record<string, string> = {
            firstName: 'First Name', middleName: 'Middle Name', lastName: 'Last Name',
            dateOfBirth: 'Date of Birth', genderId: 'Gender', ethnicityId: 'Ethnicity',
            raceIds: getRaceFieldLabel(refData.organization), relationshipId: 'Relationship',
            phoneNumber: 'Phone Number', email: 'Email', linkedClientId: 'Linked Client ID',
        };
        return labelMap[field] || field;
    };

    const normalizeFieldName = (name: string): string => {
        const hhMatch = name.match(/^hh\d+/i);
        if (hhMatch) {
            const hhPart = hhMatch[0].toLowerCase();
            const rest = name.slice(hhPart.length);
            const normalizedRest = rest
                .replace(/^[_\s-]+/, '')
                .replace(/[_\s-]+(.)/g, (_, char) => char.toUpperCase())
                .replace(/^(.)/, (m) => m.toLowerCase());
            return `${hhPart}_${normalizedRest}`;
        }
        return name
            .replace(/[_\s-]/g, '')
            .replace(/^(.)/, (match) => match.toLowerCase())
            .replace(/Id$/i, 'Id')
            .replace(/Ids$/i, 'Ids');
    };

    const autoMapColumns = (allFields: Array<{ value: string, label: string }>, refData: ReferenceData) => {
        const initialMappings: ColumnMapping[] = csvColumns.map((column) => {
            const normalized = normalizeFieldName(column.name);
            let matchedField = allFields.find(f => f.value === normalized)?.value || '';

            if (!matchedField) {
                const lowerName = column.name.toLowerCase().replace(/[_\s-]/g, '');
                const hhMatch = lowerName.match(/^hh(\d+)(.+)$/);
                if (hhMatch) {
                    const hhNumber = hhMatch[1];
                    const fieldName = hhMatch[2];
                    const hhSpecialMappings: Record<string, string> = {
                        'relationship': 'relationshipId',
                        'first': 'firstName', 'firstname': 'firstName',
                        'middle': 'middleName', 'middlename': 'middleName',
                        'last': 'lastName', 'lastname': 'lastName',
                        'dateofbirth': 'dateOfBirth', 'dob': 'dateOfBirth',
                        'phone': 'phoneNumber', 'phonenumber': 'phoneNumber',
                        'clientid': 'linkedClientId', 'linkedclientid': 'linkedClientId',
                    };
                    if (allFields.some(f => f.value === `hh${hhNumber}_genderId`)) {
                        hhSpecialMappings.gender = 'genderId';
                    }
                    if (allFields.some(f => f.value === `hh${hhNumber}_ethnicityId`)) {
                        hhSpecialMappings.ethnicity = 'ethnicityId';
                    } else if (allFields.some(f => f.value === `hh${hhNumber}_raceIds`)) {
                        hhSpecialMappings.ethnicity = 'raceIds';
                    }
                    if (allFields.some(f => f.value === `hh${hhNumber}_raceIds`)) {
                        hhSpecialMappings.race = 'raceIds';
                        hhSpecialMappings.races = 'raceIds';
                        hhSpecialMappings.raceethnicity = 'raceIds';
                    }
                    for (const [key, value] of Object.entries(hhSpecialMappings)) {
                        if (fieldName === key || fieldName.includes(key)) {
                            const hhFieldValue = `hh${hhNumber}_${value}`;
                            matchedField = allFields.find(f => f.value === hhFieldValue)?.value || '';
                            if (matchedField) break;
                        }
                    }
                } else {
                    const additionalInfoMatch = refData.clientform.find(field => {
                        if (!isMappableAdditionalInfoField(field)) {
                            return false;
                        }

                        //console.log("field.name :", field.name);
                        //console.log("field.label :", field.label);
                        //console.log("lowerName :", lowerName);

                        const fieldLabel = field.label.toLowerCase().replace(/[_\s-]/g, '');
                        const fieldName = field.name.toLowerCase().replace(/[_\s-]/g, '');
                        return fieldLabel === lowerName || fieldName === lowerName ||
                            lowerName.includes(fieldLabel) || lowerName.includes(fieldName);
                    });
                    //console.log("matchedField: ",matchedField);
                    if (additionalInfoMatch) {
                        matchedField = `additionalInfo_${additionalInfoMatch.id}`;
                    } else {
                        const specialMappings: Record<string, string> = {
                            'first': 'firstName', 'firstname': 'firstName',
                            'middle': 'middleName', 'middlename': 'middleName',
                            'last': 'lastName', 'lastname': 'lastName',
                            'dob': 'dateOfBirth', 'dateofbirth': 'dateOfBirth',
                            'phone': 'phoneNumber', 'phonenumber': 'phoneNumber',
                            'relationship': 'relationshipId',
                            'address1': 'addressLine1', 'address2': 'addressLine2',
                            'city': 'addressCity', 'state': 'addressState',
                            'zip': 'addressZipCode', 'zipcode': 'addressZipCode',
                            'postal': 'addressZipCode',
                            'program': 'programName', 'programname': 'programName',
                            'enrollment': 'enrollmentDate', 'enrollmentdate': 'enrollmentDate',
                            'activ': 'activationDate', 'activationDate': 'activationDate',
                            'householdkey': 'householdKey', 'household_key': 'householdKey',
                            'householdgroupkey': 'householdKey', 'household_group_key': 'householdKey',
                        };

                        if (allFields.some(f => f.value === 'genderId')) {
                            specialMappings.gender = 'genderId';
                        }
                        if (allFields.some(f => f.value === 'ethnicityId')) {
                            specialMappings.ethnicity = 'ethnicityId';
                        } else if (allFields.some(f => f.value === 'raceIds')) {
                            specialMappings.ethnicity = 'raceIds';
                        }
                        if (allFields.some(f => f.value === 'raceIds')) {
                            specialMappings.race = 'raceIds';
                            specialMappings.races = 'raceIds';
                            specialMappings.raceethnicity = 'raceIds';
                        }

                        if (householdIdentifiersEnabled) {
                            specialMappings.householdid = 'householdId';
                            specialMappings.household_id = 'householdId';
                        }

                        for (const [key, value] of Object.entries(specialMappings)) {
                            if (lowerName.includes(key)) {
                                matchedField = allFields.find(f => f.value === value)?.value || '';
                                if (matchedField) break;
                            }
                        }
                    }
                }

                if (!matchedField) {
                    for (const field of allFields) {
                        const lowerField = field.value.toLowerCase();
                        if (lowerName === lowerField || lowerName.includes(lowerField) || lowerField.includes(lowerName)) {
                            matchedField = field.value;
                            break;
                        }
                    }
                }
            }

            return { csvColumn: column.name, participantField: matchedField, sourceType: 'csv' as const };
        });
        
        setMappings(initialMappings);
        updateUnmappedColumns(initialMappings);
    };

    const updateUnmappedColumns = (currentMappings: ColumnMapping[]) => {
        const hasActivationDateToday = currentMappings.some(
            (mapping) => isConstantMapping(mapping) && mapping.participantField === 'activationDate' && mapping.constantValue === 'today'
        );
        const mappedCsvColumnNames = new Set(
            currentMappings
                .filter((mapping) => !isConstantMapping(mapping) && mapping.participantField)
                .map((mapping) => mapping.csvColumn)
        );
        const unmapped = csvColumns.filter((column) => {
            if (mappedCsvColumnNames.has(column.name)) {
                return false;
            }

            if (hasActivationDateToday && isActivationDateLikeColumn(column.name)) {
                return false;
            }

            return true;
        });

        setUnmappedColumns(unmapped);
    };

    const handleMappingChange = (csvColumn: string, participantField: string) => {
        const newMappings = mappings
            .map((mapping) =>
                mapping.csvColumn === csvColumn ? { ...mapping, participantField, sourceType: 'csv' as const } : mapping
            )
            .filter((mapping) => !(participantField === 'activationDate' && isConstantMapping(mapping) && mapping.participantField === 'activationDate'));
        setMappings(newMappings);
        updateUnmappedColumns(newMappings);
    };

    const handleActivationDateTodayChange = (useToday: boolean) => {
        const withoutTodayMapping = mappings.filter(
            (mapping) => !(isConstantMapping(mapping) && mapping.participantField === 'activationDate')
        );
        const newMappings = useToday
            ? [
                ...withoutTodayMapping,
                {
                    csvColumn: ACTIVATION_DATE_TODAY_COLUMN,
                    participantField: 'activationDate',
                    sourceType: 'constant' as const,
                    constantValue: 'today' as const,
                },
            ]
            : withoutTodayMapping;

        setMappings(newMappings);
        updateUnmappedColumns(newMappings);
    };

    const handleAutomap = () => {
        if (referenceData) {
            autoMapColumns(participantFields, referenceData);
            toast.success('Auto-mapping applied');
        }
    };

    const handleReset = () => {
        const resetMappings: ColumnMapping[] = csvColumns.map((column) => ({
            csvColumn: column.name,
            participantField: '',
            sourceType: 'csv',
        }));
        setMappings(resetMappings);
        updateUnmappedColumns(resetMappings);
        toast.success('Mapping reset');
    };

    // Template Management Functions
    const handleSaveTemplate = () => {
        if (!templateName.trim()) {
            toast.error('Please enter a template name');
            return;
        }

        if (templateNameExists(templateName)) {
            toast.error('A template with this name already exists');
            return;
        }

        const mappingsToSave = mappings.filter(m => m.participantField);
        
        if (mappingsToSave.length === 0) {
            toast.error('No mappings to save');
            return;
        }

        saveTemplate(templateName, mappingsToSave);
        toast.success(`Template "${templateName}" saved`);
        setTemplateName('');
        setShowSaveTemplate(false);
        loadTemplates();
    };

    const handleLoadTemplate = () => {
        if (!selectedTemplateId) {
            toast.error('Please select a template');
            return;
        }

        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) {
            toast.error('Template not found');
            return;
        }

        // Apply template mappings
        const availableFieldValues = new Set(participantFields.map(field => field.value));
        const newMappings = csvColumns.map(csvCol => {
            const templateMapping = template.mappings.find(m => m.csvColumn === csvCol.name);
            return {
                csvColumn: csvCol.name,
                participantField: getEnabledParticipantField(templateMapping?.participantField, availableFieldValues)
            };
        });

        setMappings(newMappings);
        updateUnmappedColumns(newMappings);
        updateTemplateLastUsed(selectedTemplateId);
        toast.success(`Template "${template.name}" applied`);
        setShowLoadTemplate(false);
        setSelectedTemplateId('');
    };

    const handleDeleteTemplate = (templateId: string) => {
        const template = templates.find(t => t.id === templateId);
        if (template && window.confirm(`Delete template "${template.name}"?`)) {
            deleteTemplate(templateId);
            toast.success('Template deleted');
            loadTemplates();
        }
    };

    const handleNext = () => {
        if (!referenceData) {
            toast.error('Reference data not loaded');
            return;
        }

        const mappedFields = mappings.map((m) => m.participantField).filter(Boolean);
        const requiredFields = ['firstName', 'lastName', 'activationDate'];
        if (!optionalDateOfBirthEnabled) {
            requiredFields.push('dateOfBirth');
        }
        const missingRequired = requiredFields.filter((field) => !mappedFields.includes(field));

        if (missingRequired.length > 0) {
            toast.error(`Please map required fields: ${missingRequired.map(f => 
                f === 'firstName' ? 'First Name' : 
                f === 'lastName' ? 'Last Name' :
                f === 'dateOfBirth' ? 'Date of Birth' :
                'Activation Date'
            ).join(', ')}`);
            return;
        }

        const fieldCounts: Record<string, number> = {};
        mappings.forEach((mapping) => {
            if (mapping.participantField) {
                fieldCounts[mapping.participantField] = (fieldCounts[mapping.participantField] || 0) + 1;
            }
        });

        const duplicates = Object.entries(fieldCounts).filter(([_, count]) => count > 1);
        if (duplicates.length > 0) {
            toast.error('Each field can only be mapped once. Duplicate mappings found.');
            return;
        }

        onNext(mappings.filter((m) => m.participantField && (householdIdentifiersEnabled || m.participantField !== 'householdId')), referenceData);
    };

    const getMappedColumns = () => mappings.filter((m) => m.participantField);
    const hasActivationDateCsvMapping = mappings.some(
        (mapping) => !isConstantMapping(mapping) && mapping.participantField === 'activationDate'
    );
    const hasActivationDateTodayMapping = mappings.some(
        (mapping) => isConstantMapping(mapping) && mapping.participantField === 'activationDate' && mapping.constantValue === 'today'
    );
    const stateSampleValues = `${states.slice(0, 4).map((state) => state.value).join(', ')}...`;

    if (loading) {
        return (
            <div className="import-step">
                <h2 className="section-header">Map Columns</h2>
                <div className="card margin-vertical-4">
                    <div className="card-body text-center">
                        <div className="margin-vertical-5">
                            <Loader />
                            <p className="margin-top-3">Loading reference data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="import-step">

            <div className="row justify-content-between margin-top-5 sticky-col" style={{ 
                                                                    top: '64px', 
                                                                    zIndex: 999,
                                                                    padding: '20px 5px'
                                                                }}>
                <div className="column-auto">
                    <Button id="back-button" text="Back" className="secondary" onClick={onBack} />
                </div>
                <div className="column-auto">
                    <Button
                        id="next-button"
                        text="Next"
                        icon={<i className="ri-arrow-right-line" />}
                        iconLocation="end"
                        onClick={handleNext}
                    />
                </div>
            </div>

            <h2 className="section-header">Map Columns</h2>
            <p className="margin-bottom-2">
                Match CSV columns to participant fields. Fields marked with * are required.
            </p>
            <div className="card margin-vertical-4" style={{ borderColor: '#d97706' }}>
                <div className="card-header" style={{ backgroundColor: '#fffbeb' }}>
                    <strong>Activation Date Required</strong>
                </div>
                <div className="card-body">
                    <p className="margin-bottom-3">
                        Activation Date must be mapped from a CSV column or explicitly set to today for this import.
                    </p>
                    {hasActivationDateCsvMapping ? (
                        <div className="alert alert-success">
                            Activation Date is mapped from the uploaded file.
                        </div>
                    ) : (
                        <div className="row align-items-end">
                            <div className="column">
                                <label htmlFor="activation-date-default">Activation Date Default</label>
                                <select
                                    id="activation-date-default"
                                    className="form-control"
                                    value={hasActivationDateTodayMapping ? 'today' : ''}
                                    onChange={(e) => handleActivationDateTodayChange(e.target.value === 'today')}
                                    style={{ marginTop: '0.5rem' }}
                                >
                                    <option value="">Select activation date mapping...</option>
                                    <option value="today">Set Activation Date to Today</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {householdCount > 0 && (
                <p className="margin-bottom-4" style={{ color: '#6b21a8' }}>
                    <i className="ri-team-line margin-right-1"></i>
                    <strong>Detected {householdCount} household member level(s)</strong> (HH1{householdCount > 1 ? ` - HH${householdCount}` : ''})
                </p>
            )}

            {/* Template Management Section */}
            <div className="card margin-vertical-4 hide" style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}>
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="column">
                            <strong><i className="ri-save-line margin-right-1"></i>Mapping Templates</strong>
                            <p className="margin-top-1" style={{ fontSize: '0.875rem' }}>
                                Save your current mapping as a template for reuse, or load a saved template.
                            </p>
                        </div>
                        <div className="column-auto">
                            <div className="row">
                                <div className="column-auto">
                                    <Button
                                        id="load-template-btn"
                                        text="Load Template"
                                        className="secondary small"
                                        icon={<i className="ri-folder-open-line" />}
                                        onClick={() => setShowLoadTemplate(!showLoadTemplate)}
                                        disabled={templates.length === 0}
                                    />
                                </div>
                                <div className="column-auto">
                                    <Button
                                        id="save-template-btn"
                                        text="Save Template"
                                        className="small"
                                        icon={<i className="ri-save-line" />}
                                        onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Save Template Form */}
                    {showSaveTemplate && (
                        <div className="margin-top-3" style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #d1d5db' }}>
                            <strong>Save Current Mapping as Template</strong>
                            <div className="row align-items-end margin-top-2">
                                <div className="column">
                                    <label>Template Name</label>
                                    <input
                                        type="text"
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        placeholder="e.g., Monthly Import Template"
                                        className="form-control"
                                        style={{ marginTop: '0.5rem' }}
                                    />
                                </div>
                                <div className="column-auto">
                                    <Button
                                        id="confirm-save-template"
                                        text="Save"
                                        className="small"
                                        onClick={handleSaveTemplate}
                                    />
                                </div>
                                <div className="column-auto">
                                    <Button
                                        id="cancel-save-template"
                                        text="Cancel"
                                        className="secondary small"
                                        onClick={() => {
                                            setShowSaveTemplate(false);
                                            setTemplateName('');
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Load Template Form */}
                    {showLoadTemplate && (
                        <div className="margin-top-3" style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #d1d5db' }}>
                            <strong>Load Saved Template</strong>
                            <div className="row align-items-end margin-top-2">
                                <div className="column">
                                    <label>Select Template</label>
                                    <select
                                        value={selectedTemplateId}
                                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                                        className="form-control"
                                        style={{ marginTop: '0.5rem' }}
                                    >
                                        <option value="">Choose a template...</option>
                                        {templates.map(template => (
                                            <option key={template.id} value={template.id}>
                                                {template.name} ({template.mappings.length} mappings)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="column-auto">
                                    <Button
                                        id="confirm-load-template"
                                        text="Load"
                                        className="small"
                                        onClick={handleLoadTemplate}
                                    />
                                </div>
                                <div className="column-auto">
                                    <Button
                                        id="cancel-load-template"
                                        text="Cancel"
                                        className="secondary small"
                                        onClick={() => {
                                            setShowLoadTemplate(false);
                                            setSelectedTemplateId('');
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Template List with Delete */}
                            {templates.length > 0 && (
                                <div className="margin-top-3">
                                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Saved Templates:</p>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {templates.map(template => (
                                            <div
                                                key={template.id}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '0.5rem',
                                                    marginTop: '0.5rem',
                                                    backgroundColor: '#f9fafb',
                                                    borderRadius: '4px'
                                                }}
                                            >
                                                <div>
                                                    <strong>{template.name}</strong>
                                                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                                                        {template.mappings.length} mappings • Last used: {new Date(template.lastUsed).toLocaleDateString()}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteTemplate(template.id)}
                                                    style={{
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: '#fee2e2',
                                                        color: '#dc2626',
                                                        border: '1px solid #fca5a5',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    <i className="ri-delete-bin-line"></i> Delete
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Column Mapping Table */}
            <div className="card margin-vertical-4">
                <div className="card-header">
                    <div className="row align-items-center justify-content-between">
                        <div className="column-auto">
                            <strong>Column Mapping - {csvColumns.length} columns detected</strong>
                        </div>
                        <div className="column-auto">
                            <div className="row">
                                <div className="column-auto">
                                    <Button
                                        id="automap-button"
                                        text="Automap"
                                        className="secondary small"
                                        onClick={handleAutomap}
                                    />
                                </div>
                                <div className="column-auto">
                                    <Button
                                        id="reset-mapping"
                                        text="Reset Mapping"
                                        className="secondary small"
                                        onClick={handleReset}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="table-container table-mapping">
                    <div className="margin-top-2 margin-horizontal-2 margin-vertical-4">
                        {getMappedColumns().length} columns mapped. If no unmatched column left press next button below.
                    </div>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>CSV Column</th>
                                <th>Maps To</th>
                                <th>Sample Data</th>
                                <th>Preview</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getMappedColumns().map((mapping, index) => {
                                const sampleValue = isConstantMapping(mapping) ? getTodayDisplayValue() : csvData[0]?.[mapping.csvColumn] || '';
                                const detailsValue = isConstantMapping(mapping) ? getTodayDisplayValue() : csvData[0]?.[mapping.csvColumn] || '';
                                //console.log("mapping: ", mapping)
                                // Get expected format based on field type
                                const getExpectedFormat = (fieldName: string): string => {
                                    if (!fieldName) return '';
                                    
                                    if (fieldName.includes('dateOfBirth') || fieldName.includes('enrollmentDate')) {
                                        return 'MM/dd/yyyy';
                                    }
                                    if (fieldName.includes('activationDate') || fieldName.includes('enrollmentDate')) {
                                        return 'MM/dd/yyyy';
                                    }
                                    if (fieldName.includes('genderId')) {
                                        return referenceData?.genderOptions.map(g => g.label).slice(0, 3).join(', ') + '...';
                                    }
                                    if (fieldName.includes('ethnicityId')) {
                                        return referenceData?.ethnicityOptions.map(e => e.label).slice(0, 3).join(', ') + '...';
                                    }
                                    if (fieldName.includes('raceIds')) {
                                        const text = referenceData?.raceOptions.map(r => r.label).join(', ');
                                        return text ? text.substring(0, 50) + '...' : '';
                                        //return referenceData?.races.map(r => r.name).slice(0, 3).join(', ') + '...';
                                    }
                                    if (fieldName.includes('relationshipId')) {
                                        return referenceData?.relationships.map(r => r.name).slice(0, 1).join(', ') + '...';
                                    }
                                    if (fieldName.includes('phoneNumber')) {
                                        return '(123)456-7890';
                                    }
                                    if (fieldName.includes('email')) {
                                        return 'user@example.com';
                                    }
                                    if (fieldName === 'addressState') {
                                        return stateSampleValues;
                                    }
                                    if (fieldName.includes('zipCode') || fieldName.includes('addressZipCode')) {
                                        return '12345 or 12345-6789';
                                    }
                                    if (fieldName === 'programName') {
                                        return referenceData?.programs.map(p => p.name).slice(0, 2).join(', ') + '...';
                                    }
                                    if (fieldName.includes('firstName') || fieldName.includes('lastName') || fieldName.includes('middleName')) {
                                        return 'Text';
                                    }
                                    if (fieldName.includes('address')) {
                                        return 'Text';
                                    }
                                    if (fieldName.includes('additionalInfo_')) {
                                        //console.log("referenceData: ", referenceData?.clientform);
                                        let matchedField = 'Text';
                                        let lowerName = fieldName.toLowerCase().replace(/[_\s-]/g, '');
                                        const additionalInfoMatch = referenceData?.clientform.find(field => {
                                            if (!isMappableAdditionalInfoField(field)) {
                                                return false;
                                            }

                                            const fieldLabel = field.label.toLowerCase().replace(/[_\s-]/g, '');
                                            const fieldName = field.name.toLowerCase().replace(/[_\s-]/g, '');
                                            const fieldId = field.id.toLowerCase().replace(/[_\s-]/g, '');
                                            return fieldLabel === lowerName || fieldName === lowerName || fieldId === lowerName ||
                                                lowerName.includes(fieldLabel) || lowerName.includes(fieldName) || lowerName.includes(fieldId);
                                        });
                                        //console.log("additionalInfoMatch", additionalInfoMatch);
                                        if (additionalInfoMatch) {
                                            matchedField = getFieldTypeSample(additionalInfoMatch);
                                        }
                                        return matchedField;
                                    }
                                    return 'Text';
                                };

                                return (
                                    <tr key={index}>
                                        <td><strong>{isConstantMapping(mapping) ? 'Constant Value' : mapping.csvColumn}</strong></td>
                                        <td>
                                            {isConstantMapping(mapping) ? (
                                                <strong>Activation Date = Today</strong>
                                            ) : (
                                                <Select
                                                    id={`mapping-${index}`}
                                                    value={mapping.participantField}
                                                    onChange={(value) => handleMappingChange(mapping.csvColumn, value)}
                                                >
                                                    {participantFields.map((field) => (
                                                        <option key={field.value} value={field.value}>
                                                            {field.label}
                                                        </option>
                                                    ))}
                                                </Select>
                                            )}
                                        </td>
                                        <td className="text-muted">{getExpectedFormat(mapping.participantField)}</td>
                                        <td className="text-muted">{detailsValue}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {unmappedColumns.length > 0 && (
                <div className="alert alert-warning margin-vertical-4">
                    <div className="row align-items-start">
                        <div className="column-auto">
                            <i className="ri-alert-line" style={{ fontSize: '24px' }} />
                        </div>
                        <div className="column">
                            <strong>Unmapped Columns Detected ({unmappedColumns.length})</strong>
                            <p className="margin-top-2">
                                These columns were not automatically matched. Map them manually or select "Do Not Import".
                            </p>
                            <div className="table-container margin-top-3" style={{
                                overflow: 'visible'
                            }}>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>CSV Column</th>
                                            <th>Map To</th>
                                            <th>Sample Data</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unmappedColumns.map((column, index) => {
                                            const mapping = mappings.find((m) => m.csvColumn === column.name)!;
                                            const sampleValue = csvData[0]?.[column.name] || '';

                                            return (
                                                <tr key={index}>
                                                    <td><strong>{column.name}</strong></td>
                                                    <td>
                                                        <Select
                                                            id={`unmapped-${index}`}
                                                            value={mapping.participantField}
                                                            onChange={(value) => handleMappingChange(column.name, value)}
                                                        >
                                                            {participantFields.map((field) => (
                                                                <option key={field.value} value={field.value}>
                                                                    {field.label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </td>
                                                    <td className="text-muted">{sampleValue}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="row justify-content-between margin-top-5">
                <div className="column-auto">
                    <Button id="back-button" text="Back" className="secondary" onClick={onBack} />
                </div>
                <div className="column-auto">
                    <Button
                        id="next-button"
                        text="Next"
                        icon={<i className="ri-arrow-right-line" />}
                        iconLocation="end"
                        onClick={handleNext}
                    />
                </div>
            </div>
        </div>
    );
};

export default ColumnMappingStep;
